<template>
  <div class="list-filter">
    <!--
      Bandeau de troncature : affiché quand le backend a renvoyé
      truncated=true ( cardinalité > 10 000 ). Décision Q4 + O5 :
      le bandeau apparait à l'intérieur du dropdown , ici on le rend
      au-dessus du <b-taginput> pour rester dans la même unité visuelle.

      Note : le tooltip d'aide ( ? icon ou ⓘ icon selon single-option )
      est rendu côté parent ( FiltersDataCollapse ) à côté du label de
      la colonne , pour rester aligné avec le pattern existant des
      filtres référence ( cf. lignes 70-82 de FiltersDataCollapse.vue ).
    -->
    <b-notification
      v-if="truncated"
      type="is-warning is-light"
      size="is-small"
      :closable="false"
      class="truncated-banner border-notif-warning-light"
    >
      <b-icon
        icon="exclamation-triangle"
        size="is-small"
      />
      <span>{{ $t('filter.list-truncated-warning') }}</span>
    </b-notification>
    <span v-if="options.length === 0">{{ $t("dataTable.no_filter_available") }}</span>
    <b-taginput
      v-else
      v-model="selectedValues"
      :data="filteredOptions"
      :custom-formatter="getDisplay"
      :placeholder="hasSingleOption ? '' : placeholder"
      :triggers="['focus']"
      autocomplete
      :icon="hasSingleOption ? '' : 'search'"
      :icon-right="hasSingleOption ? '' : 'angle-down'"
      open-on-focus
      type="search"
      :append-to-body="true"
      :confirm-keys="['Enter']"
      :class="{
        'scrollable-tags': selectedValues.length > 6,
        'single-option-filter': hasSingleOption && selectedValues.length > 0,
      }"
      @select="onSelect"
      @typing="onTyping"
    >
      <template #selected="props">
        <b-tag
          v-for="(tag, index) in props.tags"
          :key="index"
          closable
          :class="{ 'single-option-tag': hasSingleOption }"
          rounded
          :type="tag === EMPTY_SENTINEL ? 'is-info' : 'is-primary'"
          :aria-label="getDisplay(tag)"
          :title="hasSingleOption ? $t('filter.list-only-option-available') : getDisplay(tag)"
          @close="hasSingleOption ? undefined : removeTag(tag)"
        >
          <!-- Wrapper interne pour contraindre la largeur du TEXTE
               à 20em max sans toucher au layout flex de la b-tag
               ( la croix de fermeture reste visible et cliquable
               grâce au sizing naturel de la chip - cf. spec UX
               longues valeurs ). -->
          <span class="oa-chip-text">{{ getDisplay(tag) }}</span>
        </b-tag>
      </template>
      <template #default="slotProps">
        <OaListOptionTooltip
          :tooltip-label="getDisplay(slotProps.option)"
          :display-html="highlight(getDisplay(slotProps.option))"
        />
      </template>
    </b-taginput>
  </div>
</template>

<script lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { BIcon, BNotification, BTag, BTaginput } from "buefy";
import { highlightMatch } from "@/composable/components/highlight";
import OaListOptionTooltip from "@/components/common/OaListOptionTooltip.vue";
import i18n from "@/i18n";

/**
 * Filtre dropdown searchable sur une colonne marquée
 * {@code __FILTER_LIST__}. Calqué sur {@link ReferenceFilter} mais
 * alimenté par une liste de **valeurs distinctes** ( strings ) plutôt
 * que par des objets référence.
 *
 * <p><b>Source des options</b> : prop {@code options}, alimentée par le
 * parent ( {@code FiltersDataCollapse} ) à partir du payload
 * {@code /filters} étendu côté backend ( type
 * {@code ColumnDistinctValues} ). Une option {@code null} dans la liste
 * représente la valeur "(vide)" - voir §5.7 de FILTER_TEXT_LIST.md.
 *
 * <p><b>Sentinelle</b> : l'option {@code null} est convertie en interne
 * vers la chaine {@code EMPTY_SENTINEL} ( libellé i18n "(vide)" ) pour
 * que <b-taginput> sache l'afficher comme un tag normal. À l'émission ,
 * elle est retraduite en {@code null} sur le wire.
 *
 * <p><b>Multi-sélection</b> : OR. Plusieurs valeurs sélectionnées
 * élargissent la recherche - tooltip d'aide via icône `?`.
 *
 * <p><b>Troncature</b> : si la prop {@code truncated} est vraie , un
 * bandeau d'avertissement apparait au-dessus du dropdown ( décision
 * Q4 + O5 : 10 000 valeurs maxi côté backend ).
 */
export default {
  name: "ListFilter",
  components: { BIcon, BNotification, BTag, BTaginput, OaListOptionTooltip },
  props: {
    column: { type: Object, required: true },
    /** Valeurs distinctes ( peut contenir null pour la valeur vide ). */
    options: { type: Array as () => Array<string | null>, default: () => [] },
    /** True si le backend a tronqué la liste à DISTINCT_VALUES_LIMIT. */
    truncated: { type: Boolean, default: false },
    currentFilter: { type: Object, default: () => null },
    placeholder: { type: String, default: "" },
  },
  emits: ["update-list-filter"],
  setup(props, { emit }) {
    /**
     * Marqueur interne pour représenter la valeur "vide" dans
     * <b-taginput> ( qui n'aime pas les chaines vides ni les null
     * dans son v-model ). Traduit i18n , distinct de toute saisie
     * utilisateur plausible ( les parenthèses sont un indice visuel ).
     */
    const EMPTY_SENTINEL = computed(() => i18n.global.t("filter.empty-value-label"));

    /**
     * Convertit la liste wire ( contenant éventuellement null ) en liste
     * d'affichage ( null -> EMPTY_SENTINEL ) , triée alphabétiquement.
     */
    const sortedOptions = computed(() =>
      [...(props.options || [])]
        .map(v => v === null ? EMPTY_SENTINEL.value : v)
        .sort((a, b) => a.localeCompare(b)),
    );

    /**
     * Vrai si la colonne n'a qu'une seule valeur distincte ; on bascule
     * alors en mode "auto-select non-supprimable" , aligné sur le
     * comportement de ReferenceFilter ( cf. lignes 121-123 et la classe
     * single-option-tag ).
     */
    const hasSingleOption = computed(() => sortedOptions.value.length === 1);

    const selectedValues = ref<string[]>(
      (props.currentFilter?.filters as Array<string | null> | undefined || [])
        .map(v => v === null ? EMPTY_SENTINEL.value : v as string),
    );

    const searchText = ref("");

    /**
     * Auto-sélection au montage quand la colonne n'a qu'une seule valeur :
     * on pré-coche cette valeur et on émet le filtre pour que la
     * recherche en tienne compte d'office. Pattern repris de
     * ReferenceFilter ( setTimeout 0 pour passer au tick suivant et ne
     * pas court-circuiter le watch initial du parent ).
     */
    if (hasSingleOption.value && selectedValues.value.length === 0) {
      selectedValues.value = [...sortedOptions.value];
      setTimeout(() => emitUpdate(), 0);
    }

    watch(
      () => props.currentFilter,
      (newVal) => {
        const next = (newVal?.filters as Array<string | null> | undefined || [])
          .map(v => v === null ? EMPTY_SENTINEL.value : v as string);
        if (JSON.stringify(next) !== JSON.stringify(selectedValues.value)) {
          selectedValues.value = next;
        }
      },
      { deep: true },
    );

    function emitUpdate() {
      // Traduit la sentinelle vers null sur le wire ; les autres valeurs
      // sont émises telles quelles ( pas d'échappement regex - le
      // backend traite cela en égalité stricte multi-valeurs ).
      const wireFilters = selectedValues.value.map(v =>
        v === EMPTY_SENTINEL.value ? null : v,
      );
      emit("update-list-filter", {
        columns: props.column,
        componentKey: props.column.componentKey,
        filters: wireFilters,
      });
    }

    function onSelect() {
      // Bug fix : @select de <b-taginput> fire AVANT que le v-model
      // soit propagé à selectedValues. Si on émet immédiatement ,
      // emitUpdate lit l'ancien array sans la chip qu'on vient
      // d'ajouter , le parent met à jour currentFilter avec l'ancien ,
      // notre watch reset selectedValues à l'ancien et la chip B
      // disparaît → user ne peut jamais sélectionner plus d'1 valeur.
      // nextTick attend que le v-model soit synchronisé.
      nextTick(() => emitUpdate());
    }

    function removeTag(tag: string) {
      selectedValues.value = selectedValues.value.filter(v => v !== tag);
      emitUpdate();
    }

    /**
     * Liste calculée affichée dans la dropdown : exclut les options déjà
     * sélectionnées , filtre par le texte saisi ( insensible à la casse ).
     */
    const filteredOptions = computed(() => {
      const selected = new Set(selectedValues.value);
      const search = (searchText.value || "").toLowerCase();
      return sortedOptions.value.filter((opt) => {
        if (selected.has(opt)) return false;
        if (!search) return true;
        return opt.toLowerCase().includes(search);
      });
    });

    function onTyping(text: string) {
      searchText.value = text || "";
    }

    function getDisplay(value: string): string {
      return value;
    }

    function highlight(text: string): string {
      return highlightMatch(text, searchText.value);
    }

    return {
      EMPTY_SENTINEL: EMPTY_SENTINEL.value,
      selectedValues,
      filteredOptions,
      hasSingleOption,
      onSelect,
      removeTag,
      onTyping,
      getDisplay,
      highlight,
    };
  },
};
</script>

<style scoped>
/* Cohérence visuelle avec ReferenceFilter : limite la hauteur des tags
   au-delà de 6 entrées , scrollbar VISIBLE et fine pour que
   l'utilisateur sache qu'il peut naviguer ( scrollbar-gutter: stable
   réserve l'espace pour ne pas créer de décalage avec la dropdown
   téléportée ). */
.scrollable-tags :deep(.taginput-container) {
  max-height: 9em;
  overflow-y: auto;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
}
.scrollable-tags :deep(.taginput-container)::-webkit-scrollbar {
  width: 8px;
}
.scrollable-tags :deep(.taginput-container)::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.25);
  border-radius: 4px;
}
.scrollable-tags :deep(.taginput-container)::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.4);
}

/* Bandeau de troncature au-dessus du dropdown. Compact pour ne pas
   trop empiéter sur la zone du <b-taginput>. */
.truncated-banner {
  margin-bottom: 0.5rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

/* Mode "auto-select" quand la colonne n'a qu'une seule valeur :
   on masque le champ de saisie ( inutile - rien d'autre à choisir )
   et on grise la croix du tag pour signaler son caractère
   non-supprimable. Aligné avec le pattern de ReferenceFilter. */
.single-option-filter :deep(input) {
  display: none;
}
.single-option-tag :deep(.delete) {
  background-color: #b9bbbb;
  cursor: not-allowed;
  pointer-events: none;
  opacity: 0.6;
}

/*
 * UX longues valeurs : on cappe uniquement le TEXTE à 20em maxi
 * ( ellipse + tooltip natif via title ) , sans toucher au layout
 * flex de la b-tag rendue par Buefy. La chip elle-même garde son
 * sizing naturel ( padding + border-radius par défaut ) , la croix
 * de fermeture reste visible et cliquable , le multi-select via le
 * b-taginput fonctionne sans interférence CSS.
 *
 * Approche identique à ReferenceFilter qui ne pose AUCUN override
 * sur les b-tag et qui fonctionne ; la seule différence ici est le
 * span interne de troncature.
 */
.oa-chip-text {
  display: inline-block;
  max-width: 20em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}

/* Note : l'ellipse + tooltip natif sur les options du dropdown est
   maintenant porté par {@code OaListOptionTooltip} ( single source ) . */
</style>
